import { useEffect, useRef, useCallback, useState, } from 'react';
import { connect } from 'react-redux';

import { SET_SCHEDULE_UNLOCKED_CONFIRMATION_SHOWING } from '../actions/types';
import { siteboardAction, } from '../actions/defaultActions';

const ScheduleUnlockedConfirmationDialogue = ({ siteboardAction, }) => {
  const [isClosing, setIsClosing] = useState(false);

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_SCHEDULE_UNLOCKED_CONFIRMATION_SHOWING, {
      isOpen: false,
    });
  }, [siteboardAction]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsClosing(true);
    }, 2500);

    return () => clearTimeout(timeout);
  }, []);

  const handleExitAnimationEnd = (event) => {
    // Ignore animation events bubbling up from the lock and background.
    if (
      event.target === event.currentTarget &&
      event.animationName === 'schedule-unlock-overlay-exit'
    ) {
      closeDialogue();
    }
  };

  return (
    <>
      <style>{`
        .schedule-unlock-overlay {
          --schedule-unlock-exit-duration: 300ms;
        }

        .schedule-unlock-overlay.is-closing {
          animation: schedule-unlock-overlay-exit
            var(--schedule-unlock-exit-duration) ease-in both;
        }

        .schedule-unlock-overlay.is-closing .schedule-unlock-content {
          animation: schedule-unlock-content-exit
            var(--schedule-unlock-exit-duration)
            cubic-bezier(0.4, 0, 1, 1) both;
        }

        .schedule-unlock-pop {
          animation: schedule-unlock-pop 450ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .schedule-unlock-shackle {
          transform-box: view-box;
          transform-origin: 12px 19px;
          animation: schedule-unlock-shackle 450ms 150ms ease-out both;
        }

        .schedule-unlock-ring {
          animation: schedule-unlock-ring 650ms 300ms ease-out both;
        }

        .schedule-unlock-spark {
          animation: schedule-unlock-spark 650ms 300ms ease-out both;
        }

        .schedule-unlock-copy {
          animation: schedule-unlock-copy 300ms 350ms ease-out both;
        }

        .schedule-unlock-background {
          transform-origin: center;
          animation: schedule-unlock-background-expand 550ms
            cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes schedule-unlock-overlay-exit {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        @keyframes schedule-unlock-content-exit {
          from { transform: scale(1); opacity: 1; }
          to { transform: scale(0.65); opacity: 0; }
        }

        @keyframes schedule-unlock-pop {
          0% { opacity: 0; transform: scale(0.65); }
          65% { opacity: 1; transform: scale(1.12); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes schedule-unlock-shackle {
          from { transform: translateY(0) rotate(0deg); }
          to { transform: translateY(-4px) rotate(-25deg); }
        }

        @keyframes schedule-unlock-ring {
          from { opacity: 0.8; transform: scale(0.9); }
          to { opacity: 0; transform: scale(1.8); }
        }

        @keyframes schedule-unlock-spark {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(-22px) scale(0);
          }
          25% { opacity: 1; }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(-55px) scale(0.5);
          }
        }

        @keyframes schedule-unlock-copy {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes schedule-unlock-background-expand {
          from { transform: scale(0.12); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .schedule-unlock-overlay {
            --schedule-unlock-exit-duration: 1ms;
          }

          .schedule-unlock-overlay.is-closing .schedule-unlock-content,
          .schedule-unlock-pop,
          .schedule-unlock-copy,
          .schedule-unlock-background {
            animation: none;
          }

          .schedule-unlock-shackle {
            animation: none;
            transform: translateY(-4px) rotate(-25deg);
          }

          .schedule-unlock-ring,
          .schedule-unlock-spark {
            display: none;
          }
        }
      `}</style>

      <div
        className={`schedule-unlock-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 ${
          isClosing ? 'is-closing' : ''
        }`}
        role='status'
        aria-live='polite'
        onAnimationEnd={handleExitAnimationEnd}
      >
        <div className='schedule-unlock-content relative isolate flex aspect-square w-[21rem] max-w-full flex-col items-center justify-center gap-5 p-8'>
          {/* Expanding circular background */}
          <div
            className='schedule-unlock-background pointer-events-none absolute inset-0 -z-10 rounded-full bg-neutral-900 shadow-2xl'
            aria-hidden='true'
          />

          <div
            className='schedule-unlock-pop relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-emerald-400/10'
            aria-hidden='true'
          >
            <div className='schedule-unlock-ring absolute inset-0 rounded-full border-2 border-emerald-400/60' />

            {Array.from({ length: 8 }, (_, index) => (
              <div
                key={index}
                className='pointer-events-none absolute inset-0'
                style={{ transform: `rotate(${index * 45}deg)` }}
              >
                <span
                  className='schedule-unlock-spark absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full'
                  style={{
                    backgroundColor: index % 2 ? '#f9a928' : '#34d399',
                  }}
                />
              </div>
            ))}

            <svg
              width='36'
              height='36'
              viewBox='0 0 40 40'
              fill='none'
              className='relative overflow-visible text-emerald-400'
            >
              <path
                className='schedule-unlock-shackle'
                d='M12 19V12a8 8 0 0 1 16 0v7'
                stroke='currentColor'
                strokeWidth='3'
                strokeLinecap='round'
              />
              <rect
                x='8'
                y='18'
                width='24'
                height='18'
                rx='5'
                className='fill-neutral-900'
                stroke='currentColor'
                strokeWidth='3'
              />
              <circle cx='20' cy='26' r='2' fill='currentColor' />
              <path
                d='M20 27v3'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          </div>

          <div className='schedule-unlock-copy text-center'>
            <div className='font-semibold text-white'>
              Schedule unlocked
            </div>
            <div className='mt-1 text-white/60'>
              You’re ready to make changes.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
}};
export default connect(mapStateToProps, { siteboardAction })(ScheduleUnlockedConfirmationDialogue);
